(() => {
  if (customElements.get('reise-collection')) return;

  class ReiseCollection extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('.rn-col__filters');
      this.initialOffset = Number(this.querySelector('[data-rn-pagination]')?.dataset.offset || 0);
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.addEventListener('click', this.onClick, options);
      this.addEventListener('submit', this.onSubmit, options);
      this.addEventListener('input', this.onInput, options);
      this.dialog?.addEventListener('close', this.onDialogClose, options);
      this.dialog?.addEventListener('cancel', this.onDialogClose, options);
      this.mobileQuery.addEventListener('change', this.updateColumnButtons, options);
      this.preserveSearchParameters();
      this.updateColumnButtons();
      this.updatePriceButtons();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('rn-collection-filter-open');
    }

    onDialogClose = () => {
      document.documentElement.classList.remove('rn-collection-filter-open');
    };

    onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const openButton = target.closest('[data-rn-filter-open]');
      if (openButton && this.dialog) {
        this.dialog.showModal();
        document.documentElement.classList.add('rn-collection-filter-open');
        return;
      }
      if (target.closest('[data-rn-filter-close]')) {
        this.dialog?.close();
        return;
      }
      if (target === this.dialog) {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
        return;
      }
      const columnButton = target.closest('[data-rn-columns]');
      if (columnButton) {
        const columns = columnButton.dataset.rnColumns;
        if (this.mobileQuery.matches) this.dataset.mobileColumns = columns;
        else this.dataset.columns = columns;
        this.updateColumnButtons();
        return;
      }
      const priceButton = target.closest('[data-rn-price-min][data-rn-price-max]');
      if (priceButton) {
        const fieldset = priceButton.closest('fieldset');
        const minInput = fieldset?.querySelector('[data-rn-price-min-input]');
        const maxInput = fieldset?.querySelector('[data-rn-price-max-input]');
        if (!minInput || !maxInput) return;
        minInput.value = priceButton.dataset.rnPriceMin;
        maxInput.value = priceButton.dataset.rnPriceMax;
        this.querySelector('.rn-col__filter-apply').textContent = 'Ver produtos';
        this.updatePriceButtons();
        return;
      }
      const loadButton = target.closest('[data-rn-load-more]');
      if (loadButton && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        if (loadButton.getAttribute('aria-disabled') !== 'true') this.loadMore(loadButton);
      }
    };

    onInput = (event) => {
      if (event.target.matches('[data-rn-price-min-input], [data-rn-price-max-input]')) this.updatePriceButtons();
      if (event.target.closest('[data-rn-filter-form]') && event.target.name !== 'sort_by') this.querySelector('.rn-col__filter-apply').textContent = 'Ver produtos';
    };

    onSubmit = (event) => {
      if (!event.target.matches('[data-rn-filter-form]')) return;
      const form = event.target;
      if (!form.checkValidity()) return;
      event.preventDefault();
      const url = new URL(form.action, window.location.origin);
      for (const [name, value] of new FormData(form)) {
        if (String(value).trim() !== '') url.searchParams.append(name, value);
      }
      window.location.assign(url.href);
    };

    preserveSearchParameters() {
      if (this.dataset.catalogueType !== 'search') return;
      const form = this.querySelector('[data-rn-filter-form]');
      const current = new URL(window.location.href).searchParams;
      // Shopify can serialize these options as a hash in pagination URLs.
      const serialized = current.get('options');
      if (serialized) {
        try {
          const parsed = JSON.parse(serialized.replace(/=>/g, ':'));
          const options = [
            ['prefix', parsed.prefix, ['last', 'none']],
            ['unavailable_products', parsed.unavailable_products ?? parsed.unavailable, ['show', 'hide', 'last']]
          ];
          for (const [key, value, allowed] of options) {
            const name = `options[${key}]`;
            if (allowed.includes(value) && !current.has(name)) current.set(name, value);
          }
        } catch {
          // Explicit bracket parameters still work when the hash is invalid.
        }
      }
      const names = [...new Set(['q', 'type', 'options[prefix]', 'options[unavailable_products]', 'options[unavailable]',
        ...Array.from(current.keys()).filter(name => /^options\[[^\]]+\]$/.test(name))])];
      for (const name of names) {
        let input = form?.elements.namedItem(name);
        if (current.has(name)) {
          if (!input && form) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.append(input);
          }
          if (input) input.value = current.get(name);
        }
      }
      for (const link of this.querySelectorAll('[data-rn-catalogue-link]')) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.delete('options');
        url.searchParams.delete('section_id');
        for (const name of names) {
          const input = form?.elements.namedItem(name);
          if (input?.value) url.searchParams.set(name, input.value);
        }
        link.href = url.href;
      }
    }

    updateColumnButtons = () => {
      const selected = this.mobileQuery.matches ? this.dataset.mobileColumns : this.dataset.columns;
      for (const button of this.querySelectorAll('[data-rn-columns]')) button.setAttribute('aria-pressed', String(button.dataset.rnColumns === selected));
      this.updateImageSizes();
    };

    updateImageSizes(root = this.querySelector('[data-rn-product-grid]')) {
      const mobile = this.dataset.mobileColumns === '1' ? '100vw' : 'calc(50vw - 1px)';
      const desktop = this.dataset.columns === '2' ? 'calc(50vw - 1px)' : 'calc(25vw - 1.5px)';
      const sizes = `(max-width: 767px) ${mobile}, ${desktop}`;
      for (const image of root?.querySelectorAll('.rn-card__image') || []) image.sizes = sizes;
    }

    updatePriceButtons() {
      for (const group of this.querySelectorAll('.rn-col__filter-group--price')) {
        const min = group.querySelector('[data-rn-price-min-input]')?.value ?? '';
        const max = group.querySelector('[data-rn-price-max-input]')?.value ?? '';
        for (const button of group.querySelectorAll('[data-rn-price-min][data-rn-price-max]')) {
          button.setAttribute('aria-pressed', String(min === button.dataset.rnPriceMin && max === button.dataset.rnPriceMax));
        }
      }
    }

    async loadMore(button) {
      const pagination = this.querySelector('[data-rn-pagination]');
      const status = pagination.querySelector('[data-rn-load-status]');
      const nextPage = new URL(button.href, window.location.origin);
      if (this.dataset.catalogueType === 'search') {
        nextPage.searchParams.delete('options');
        nextPage.searchParams.delete('section_id');
      }
      const requestUrl = new URL(nextPage);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      this.request = new AbortController();
      button.setAttribute('aria-disabled', 'true');
      button.textContent = 'Carregando…';
      const resultLabel = this.dataset.resultLabel || 'produtos';
      status.textContent = `Carregando mais ${resultLabel}.`;
      try {
        const response = await fetch(requestUrl, { signal: this.request.signal });
        if (!response.ok) throw new Error('Falha ao carregar o catálogo');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextCollection = documentFragment.getElementById(this.id);
        const nextGrid = nextCollection?.querySelector('[data-rn-product-grid]');
        const nextPagination = nextCollection?.querySelector('[data-rn-pagination]');
        if (!nextGrid || !nextPagination) throw new Error('Resposta de catálogo incompleta');
        const currentGrid = this.querySelector('[data-rn-product-grid]');
        const cards = [...nextGrid.children];
        this.updateImageSizes(nextGrid);
        currentGrid.append(...cards);
        pagination.replaceWith(nextPagination);
        this.preserveSearchParameters();
        nextPagination.querySelector('[data-rn-load-status]').textContent = `${cards.length} ${resultLabel} carregados.`;
        const shown = this.initialOffset + currentGrid.children.length;
        nextPagination.querySelector('[data-rn-product-count]').textContent = `Exibindo ${shown} de ${nextPagination.dataset.total} ${resultLabel}`;
        const firstLink = cards[0]?.querySelector('a[href]');
        firstLink?.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('reise:products-loaded', { bubbles: true }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        button.removeAttribute('aria-disabled');
        button.textContent = 'Carregar mais';
        status.textContent = `Não foi possível carregar os ${resultLabel}. Abrindo a próxima página.`;
        window.location.assign(nextPage.href);
      }
    }
  }

  customElements.define('reise-collection', ReiseCollection);
})();
